import type { DestinationMetadata } from '@/scopes/search/quick-search/domain/types'
import type { FilterOption } from '@/scopes/search/types'
import type { ChangeEventHandler } from 'react'
import { useIntl } from '@/intl'
import { DestinationNoResult } from '@/scopes/search/components/DestinationFilter/DestinationNoResult'
import { Destination } from '@/scopes/search/components/DestinationFilter/DestinationOption'
import { getFilteredOptions } from '@/scopes/search/components/DestinationFilter/optionUtils'
import { space, tok } from '@sunwebgroup/ui-components'
import { SearchField } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: ${space('primitives_space_16')};
`

export const Footer = styled.div`
  background-color: ${tok('semantics_views_background_color')};
  bottom: 0;
  display: flex;
  padding: ${space('primitives_space_16')};
  position: -webkit-sticky;
  position: sticky;
  width: 100%;
  z-index: 1;
`

interface FilterProps {
  options: FilterOption<string, DestinationMetadata>[]
  defaultOptions: FilterOption<string, DestinationMetadata>[]
  onItemClick: (selection: FilterOption<string, DestinationMetadata>) => void
}

export function Root({ options, defaultOptions, onItemClick }: FilterProps) {
  const intl = useIntl()
  const [inputValue, setInputValue] = React.useState('')
  const [filteredOptions, setFilteredOptions]
    = React.useState<FilterOption<string, DestinationMetadata>[]>(defaultOptions)

  const filterOptions = React.useCallback(
    (newValue: string) => {
      if (newValue.length < 2) {
        setFilteredOptions(defaultOptions)
        return
      }

      const result = getFilteredOptions(options, newValue)
      setFilteredOptions(result)
    },
    [defaultOptions, options],
  )

  const onChange: ChangeEventHandler = React.useCallback(
    (e) => {
      const { value } = e.target as HTMLInputElement
      setInputValue(value)
      filterOptions(value)
    },
    [filterOptions],
  )

  return (
    <Content>
      <SearchField
        value={inputValue}
        placeholder={intl.formatMessage({ id: 'quicksearch_filter_destination_search_placeholder' })}
        onChange={onChange}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus
        clearButtonAriaLabel={intl.formatMessage({ id: 'clear_search_button_aria_label' })}
      />
      {filteredOptions.length > 0
        ? (
            filteredOptions.map(option => (
              <Destination key={option.value} option={option} onItemClick={onItemClick} query={inputValue} />
            ))
          )
        : (
            <DestinationNoResult inputValue={inputValue} />
          )}
    </Content>
  )
}
